import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, Circle, Copy, ExternalLink, Link2, RefreshCw, Unplug } from "lucide-react";

const err = (e, f) => toast.error(e?.response?.data?.detail || f);

function Step({ done, n, title, children }) {
  return (
    <li className="flex gap-3" data-testid={`ebay-setup-step-${n}`}>
      {done ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" /> : <Circle className="w-5 h-5 text-slate-300 shrink-0 mt-0.5" />}
      <div className="text-sm text-slate-600 min-w-0 flex-1"><p className="font-semibold text-slate-900">{n}. {title}</p>{children}</div>
    </li>
  );
}

export function CopyLine({ label, value, testid }) {
  return (
    <div className="mt-1.5 flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-1.5 text-xs" data-testid={testid}>
      <span className="text-slate-500 shrink-0">{label}</span>
      <code className="truncate text-slate-800">{value}</code>
      <button className="ml-auto text-slate-400 hover:text-slate-700" onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }}><Copy className="w-3.5 h-3.5" /></button>
    </div>
  );
}

export function EbaySetupGuide({ st }) {
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="ebay-setup-guide">
      <h2 className="text-base font-bold text-slate-900 mb-1">Set up your eBay developer account</h2>
      <p className="text-sm text-slate-500 mb-4">One-off setup, about 30 minutes. eBay approves production keys after the deletion endpoint is verified.</p>
      <ol className="space-y-4">
        <Step n={1} title="Create a developer account" done={st.configured}>
          Sign up at <a className="text-indigo-600 underline" href="https://developer.ebay.com/signin" target="_blank" rel="noreferrer">developer.ebay.com</a> using your business details (this is separate from your seller login).</Step>
        <Step n={2} title="Set up account-deletion notices" done={st.deletion_ready}>
          In <b>Alerts &amp; Notifications → Marketplace Account Deletion</b>, enter this endpoint and make up a verification token (32–80 letters/numbers). Keep the token — you'll add it here in step 5.
          <CopyLine label="Endpoint" value={st.urls.deletion} testid="ebay-deletion-url" /></Step>
        <Step n={3} title="Create a Production keyset" done={st.configured}>
          In <b>Application Keys</b>, create a Production keyset. Note the <b>App ID</b> (Client ID) and <b>Cert ID</b> (Client Secret).</Step>
        <Step n={4} title="Create your sign-in link (RuName)" done={st.configured}>
          Under <b>User Tokens → Get a Token from eBay via Your Application</b>, add a redirect. Set the accepted URL to this address, then copy the generated <b>RuName</b>.
          <CopyLine label="Accepted URL" value={st.urls.callback} testid="ebay-callback-url" /></Step>
        <Step n={5} title="Add the keys to TedOps" done={st.configured && st.deletion_ready}>
          Send the App ID, Cert ID, RuName and verification token through the secure keys card — never paste them into chat or email.</Step>
        <Step n={6} title="Connect each eBay seller account" done={st.accounts.some((a) => a.status === "connected")}>
          Choose <b>Sandbox</b> or <b>Production</b> above, click <b>Connect account</b> and sign in to eBay. Read-only: TedOps can't change your listings or stock.</Step>
      </ol>
    </section>
  );
}

function ImportProgress({ accountId, active, onFinished }) {
  const [run, setRun] = useState(null);
  const poll = useCallback(() => api.get(`/ebay/accounts/${accountId}/runs`).then((r) => setRun(r.data.items[0] || null)).catch(() => {}), [accountId]);
  useEffect(() => { poll(); }, [poll, active]);
  const live = run && ["running", "matching"].includes(run.status);
  useEffect(() => {
    if (!live) { return undefined; }
    const t = setInterval(poll, 2000);
    return () => clearInterval(t);
  }, [live, poll]);
  useEffect(() => {
    if (run && run.status === "done" && active) { onFinished(run); }
  }, [run, active, onFinished]);
  if (!run) return null;
  const pct = run.status === "done" ? 100 : run.status === "matching" ? 95 : run.pages ? Math.round((run.page / run.pages) * 90) : 5;
  return (
    <div className="w-full mt-2" data-testid={`ebay-import-progress-${accountId}`}>
      {live && <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-indigo-500 transition-[width] duration-500" style={{ width: `${pct}%` }} data-testid="ebay-import-bar" /></div>}
      <p className="text-xs text-slate-500 mt-1" data-testid="ebay-import-status">
        {run.status === "running" && `Importing… page ${run.page || 0}${run.pages ? ` of ${run.pages}` : ""} · ${run.lines || 0} lines read · ${run.new_lines || 0} new`}
        {run.status === "matching" && `Matching ${run.lines || 0} lines to your products…`}
        {run.status === "done" && <span><b className="text-emerald-700" data-testid="ebay-import-new">{run.new_lines || 0} new</b> since last import · {run.lines || 0} lines in total</span>}
        {run.status === "failed" && <span className="text-rose-600">Import failed: {run.error}</span>}
        {run.status === "skipped" && <span className="text-amber-700">{run.error}</span>}
      </p>
    </div>
  );
}

function AccountRow({ a, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(0);
  const finished = useCallback((run) => { setActive(0); toast.success(`Import finished: ${run.new_lines || 0} new listing lines`); onChanged(); }, [onChanged]);
  const run = async (path, msg) => {
    setBusy(true);
    try { const r = await api.post(`/ebay/accounts/${a.id}/${path}`); toast.success(msg); if (r.data.run_id) { setActive(Date.now()); } else { onChanged(r.data); } }
    catch (e) { err(e, "Action failed"); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 py-3 border-t border-slate-100 first:border-0" data-testid={`ebay-account-${a.id}`}>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900">{a.label || a.ebay_username}
          <span className={`ml-2 text-[11px] font-medium rounded-full px-2 py-0.5 border ${a.sample ? "bg-amber-50 text-amber-700 border-amber-200" : a.status === "connected" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
            {a.sample ? "Sample data" : a.status}</span>
          {!a.sample && <span className="ml-1.5 text-[11px] font-semibold capitalize rounded-full px-2 py-0.5 bg-slate-100 text-slate-700" data-testid={`ebay-account-env-${a.id}`}>{ebayEnvLabel(a.environment)}</span>}</p>
        <p className="text-xs text-slate-500">{a.last_import_at ? `Last import ${new Date(a.last_import_at).toLocaleString("en-GB")} · ${a.last_import_lines || 0} lines${a.last_import_new != null ? ` · ${a.last_import_new} new` : ""}` : "Not imported yet"}</p>
      </div>
      <div className="ml-auto flex gap-2">
        <Button size="sm" variant="outline" disabled={busy || (!a.sample && a.status !== "connected")} onClick={() => run("import", a.sample ? "Matching re-run" : "Import started")} data-testid={`ebay-import-${a.id}`}>
          <RefreshCw className="w-4 h-4 mr-1.5" />{a.sample ? "Re-run matching" : "Import listings"}</Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => run("disconnect", a.sample ? "Sample removed" : "Disconnected")} data-testid={`ebay-disconnect-${a.id}`}>
          <Unplug className="w-4 h-4 mr-1.5" />{a.sample ? "Remove sample" : "Disconnect"}</Button>
      </div>
      {!a.sample && <ImportProgress accountId={a.id} active={active} onFinished={finished} />}
    </div>
  );
}

export const ebayEnvLabel = (env) => (env === "production" ? "Production" : "Sandbox");

export async function startEbayConnect(env, returnTo) {
  try {
    const r = await api.post("/ebay/connect", { environment: env === "production" ? "production" : "sandbox", return_to: returnTo });
    window.location.href = r.data.authorization_url;
  } catch (e) { toast.error(e.response?.data?.detail || "Could not start eBay sign-in"); }
}

export function EbayAccounts({ st, isSuper, reload }) {
  const [env, setEnv] = useState("sandbox");
  const envOk = !!st.environments?.[env]?.configured;
  const connect = () => startEbayConnect(env, "/marketplace/ebay");
  const sample = async () => {
    try { await api.post("/ebay/sample"); toast.success("Sample listings loaded"); reload(); } catch (e) { err(e, "Could not load sample"); }
  };
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="ebay-accounts">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <h2 className="text-base font-bold text-slate-900">eBay seller accounts</h2>
        <div className="sm:ml-auto flex flex-wrap gap-2">
          {isSuper && !st.accounts.some((a) => a.sample) && <Button size="sm" variant="outline" onClick={sample} data-testid="ebay-load-sample">Try with sample data</Button>}
          <div className="inline-flex rounded-lg border border-slate-200 p-0.5" role="radiogroup" data-testid="ebay-connect-env">
            {["sandbox", "production"].map((e) => (
              <button key={e} type="button" role="radio" aria-checked={env === e} onClick={() => setEnv(e)} data-testid={`ebay-connect-env-${e}`}
                className={`px-2.5 py-1 text-xs font-semibold capitalize rounded-md transition-colors ${env === e ? (e === "production" ? "bg-rose-600 text-white" : "bg-slate-900 text-white") : "text-slate-600 hover:bg-slate-100"}`}>{e}</button>))}
          </div>
          <Button size="sm" disabled={!envOk} onClick={connect} data-testid="ebay-connect-btn"><Link2 className="w-4 h-4 mr-1.5" />Connect {ebayEnvLabel(env)} account</Button>
        </div>
      </div>
      {!envOk && <p className="text-xs text-amber-700 mb-2" data-testid="ebay-not-configured">Connecting to {env} is available once its developer keys and RuName are added.</p>}
      {env === "production" && envOk && <p className="text-xs text-rose-700 mb-2" data-testid="ebay-production-note">Production: signs in on ebay.com with your real seller account. Read-only — nothing is listed, changed or imported.</p>}
      {st.accounts.length === 0 ? <p className="text-sm text-slate-400 py-3">No accounts connected yet.</p>
        : st.accounts.map((a) => <AccountRow key={a.id} a={a} onChanged={reload} />)}
    </section>
  );
}

export function EbaySettings({ st, reload, canManage }) {
  const [buffer, setBuffer] = useState(String(st.settings.buffer));
  const [max, setMax] = useState(st.settings.max_qty == null ? "" : String(st.settings.max_qty));
  const save = async () => {
    try { await api.put("/ebay/settings", { buffer: Number(buffer || 0), max_qty: max === "" ? null : Number(max) }); toast.success("eBay stock settings saved"); reload(); }
    catch (e) { err(e, "Could not save"); }
  };
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="ebay-settings">
      <h2 className="text-base font-bold text-slate-900">Marketplace settings — eBay stock</h2>
      <p className="text-sm text-slate-500 mb-4">eBay quantity = TedOps available-to-sell (after reserved and allocated) minus the buffer, capped at the maximum. Products can override both.</p>
      <div className="flex flex-wrap items-end gap-4">
        <label className="text-xs text-slate-500">Stock buffer (units)<Input type="number" min="0" value={buffer} disabled={!canManage} onChange={(e) => setBuffer(e.target.value)} className="mt-1 h-9 w-32" data-testid="ebay-setting-buffer" /></label>
        <label className="text-xs text-slate-500">Max listing quantity<Input type="number" min="0" value={max} placeholder="Unlimited" disabled={!canManage} onChange={(e) => setMax(e.target.value)} className="mt-1 h-9 w-32" data-testid="ebay-setting-max" /></label>
        {canManage && <Button onClick={save} data-testid="ebay-settings-save">Save</Button>}
      </div>
      <p className="mt-4 text-xs font-medium text-slate-600 inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1" data-testid="ebay-sync-off">
        Stock sync to eBay: OFF — read-only until you approve live sync</p>
      <a className="ml-3 text-xs text-indigo-600 inline-flex items-center gap-1" href="https://developer.ebay.com/my/keys" target="_blank" rel="noreferrer">eBay keys <ExternalLink className="w-3 h-3" /></a>
    </section>
  );
}
